import type { RefundRequestDetail, RefundRequestMessage } from "@/@types/RefundRequest";
import BadgeShared from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { RefundRequestsService } from "@/services/refund-requests.service";
import { format } from "date-fns";
import { AlertTriangle, Check, ChevronDown, Clock, FileText, Paperclip, X } from "lucide-react";
import { KeyboardEvent, ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSendRefundMessage } from "../hooks/useRefundRequests";
import {
  attachmentError,
  mediationSteps,
  refundRequestBadgeStatus,
  refundRequestWarrantyState,
  responseTimeLeft,
  type MediationStepState,
  type TimeLeft,
} from "../refund-request.utils";
import { InfoTooltip } from "./InfoTooltip";

const formatDate = (value: string | null | undefined, pattern = "dd/MM/yyyy") =>
  value ? format(new Date(value), pattern) : "—";

export function RefundRequestTimeline({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation(["common", "disputes"]);
  const method = request.payment_method
    ? t(`common:${request.payment_method}`, { defaultValue: request.payment_method })
    : "—";

  const timelineLabel = (key: string) =>
    key === "sale_approved"
      ? t("disputes:timeline.sale_approved", { method })
      : t(`disputes:timeline.${key}`, { defaultValue: t("disputes:timeline.unknown") });

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{t("disputes:detail.timeline")}</h3>
      <ol className="space-y-2">
        {request.timeline.map((step) => (
          <li key={step.key} className="flex gap-3 text-sm">
            <span className="w-24 shrink-0 text-muted-foreground">{formatDate(step.at)}</span>
            <span>{timelineLabel(step.key)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

async function downloadAttachment(requestId: string, message: RefundRequestMessage) {
  if (message.id === null || !message.attachment) return;

  const blob = await RefundRequestsService.downloadAttachment(requestId, message.id);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = message.attachment.name;
  link.click();
  URL.revokeObjectURL(url);
}

export function RefundRequestConversation({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation(["common", "disputes"]);

  const handleDownload = (message: RefundRequestMessage) =>
    downloadAttachment(request.id, message).catch(() =>
      toast({ description: t("disputes:mediation.download_error"), variant: "destructive" }),
    );

  return (
    <ol className="space-y-3">
      {request.messages.map((message, index) => {
        const isSeller = message.author === "seller";

        return (
          <li key={message.id ?? `request-${index}`} className={cn("flex", isSeller && "justify-end")}>
            <div
              className={cn(
                "max-w-[85%] rounded-lg border p-3 text-sm space-y-1",
                isSeller ? "bg-primary/10 border-primary/30" : "bg-muted/30",
              )}
            >
              <span className="block text-xs text-muted-foreground">
                {isSeller
                  ? t("disputes:mediation.author_seller")
                  : t("disputes:mediation.author_buyer", { name: request.sale.buyer_name ?? "—" })}
                {" · "}
                {formatDate(message.sent_at, "dd/MM HH:mm")}
              </span>
              {message.kind === "request" && (
                <span className="block font-medium">
                  {t("disputes:mediation.request_label")}
                  {message.reason ? `: ${message.reason}` : ""}
                </span>
              )}
              {message.body && <p className="whitespace-pre-line break-words">{message.body}</p>}
              {message.attachment && (
                <button
                  type="button"
                  onClick={() => handleDownload(message)}
                  className="mt-1 inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs hover:bg-secondary/50"
                >
                  <FileText size={14} />
                  <span className="truncate max-w-[180px]">{message.attachment.name}</span>
                  <span className="text-muted-foreground">{formatSize(message.attachment.size)}</span>
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function StepDot({ state, number }: { state: MediationStepState; number: number }) {
  return (
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold",
        state === "done" && "border-green-500 bg-green-500/20 text-green-400",
        state === "current" && "border-yellow-500 bg-yellow-500/20 text-yellow-300",
        state === "waiting" && "border-muted-foreground text-muted-foreground",
      )}
    >
      {state === "done" ? <Check size={12} /> : number}
    </span>
  );
}

function TurnNotice({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation("disputes");
  const isSellerTurn = request.mediation_stage === "seller_turn";
  const dueAt = isSellerTurn ? request.response_due_at : request.buyer_response_due_at;
  const left = responseTimeLeft(dueAt);

  if (!request.mediation_stage || !dueAt || !left) return null;

  const leftText = (value: TimeLeft) =>
    value.unit === "overdue" ? t("detail.time_left_overdue") : t(`detail.time_left_${value.unit}`, { count: value.value });
  const values = { date: formatDate(dueAt, "dd/MM HH:mm"), left: leftText(left) };

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border p-3 text-sm",
        isSellerTurn
          ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-200"
          : "border-blue-500/30 bg-blue-500/10 text-blue-200",
      )}
    >
      {isSellerTurn ? (
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      ) : (
        <Clock size={16} className="shrink-0 mt-0.5" />
      )}
      <span className="flex-1">
        <strong>{t(isSellerTurn ? "mediation.seller_turn_label" : "mediation.buyer_turn_label")}</strong>{" "}
        {t(isSellerTurn ? "mediation.seller_turn" : "mediation.buyer_turn", values)}
      </span>
    </div>
  );
}

function MessageComposer({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation(["common", "disputes"]);
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { mutate: send, isPending } = useSendRefundMessage();

  const handleFile = (file: File | undefined) => {
    if (fileInput.current) fileInput.current.value = "";
    if (!file) return;

    const error = attachmentError(file);
    if (error) {
      toast({ description: t(`disputes:mediation.attachment_${error}`), variant: "destructive" });
      return;
    }
    setAttachment(file);
  };

  const submit = () => {
    if (!body.trim() || isPending) return;

    send(
      { id: request.id, body: body.trim(), attachment },
      {
        onSuccess: () => {
          setBody("");
          setAttachment(null);
        },
        onError: (error: any) =>
          toast({
            description: error?.response?.data?.message || t("common:data_submission_error"),
            variant: "destructive",
          }),
      },
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="space-y-2">
      {attachment && (
        <div className="inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
          <FileText size={14} />
          <span className="truncate max-w-[220px]">{attachment.name}</span>
          <button
            type="button"
            onClick={() => setAttachment(null)}
            aria-label={t("disputes:mediation.remove_attachment")}
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInput.current?.click()}
          disabled={isPending}
          aria-label={t("disputes:mediation.attach")}
        >
          <Paperclip size={16} />
        </Button>
        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          maxLength={2000}
          className="min-h-0 resize-none"
          placeholder={t("disputes:mediation.placeholder")}
          disabled={isPending}
        />
        <Button type="button" onClick={submit} disabled={!body.trim() || isPending}>
          {t("disputes:mediation.send")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("disputes:mediation.composer_hint")}</p>
    </div>
  );
}

interface RefundRequestMediationProps {
  request: RefundRequestDetail;
  canRespond: boolean;
  onApprove: () => void;
  details: ReactNode;
}

export function RefundRequestMediation({ request, canRespond, onApprove, details }: RefundRequestMediationProps) {
  const { t } = useTranslation(["common", "disputes"]);
  const steps = mediationSteps(request.mediation_stage);
  const warrantyState = refundRequestWarrantyState(request);

  return (
    <>
      <header className="flex flex-col gap-2 pr-8">
        <span className="text-xs text-muted-foreground">
          {request.code} · {t("disputes:types.refunds")}
        </span>
        <h2 className="text-2xl font-semibold">{request.amount_formatted}</h2>
        <span className="text-sm text-muted-foreground">
          {request.sale.product ?? "—"} · {request.sale.buyer_name ?? "—"}
        </span>
        <div className="flex items-center gap-3">
          <BadgeShared
            status={refundRequestBadgeStatus(request.status)}
            text={t(`disputes:status.${request.status}`)}
          />
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs text-muted-foreground",
              warrantyState === "ending" && "text-red-500",
            )}
          >
            {t("disputes:detail.warranty")}:{" "}
            {warrantyState === "closed" || warrantyState === "expired"
              ? t("disputes:warranty_expired")
              : t("disputes:days_left", { count: request.warranty_days_left ?? 0 })}
            <InfoTooltip text={t("disputes:warranty_tooltip")} />
          </span>
        </div>
      </header>

      <RefundRequestTimeline request={request} />

      <section className="flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-2">
          <StepDot state={steps.seller} number={1} />
          {t("disputes:mediation.step_seller")}
        </span>
        <span className="h-px flex-1 bg-border" />
        <span className="inline-flex items-center gap-2">
          <StepDot state={steps.buyer} number={2} />
          {t("disputes:mediation.step_buyer")}
        </span>
      </section>

      <TurnNotice request={request} />

      <Collapsible className="rounded-md border">
        <CollapsibleTrigger className="group flex w-full items-center justify-between p-3 text-sm font-medium">
          {t("disputes:mediation.order_details")}
          <ChevronDown size={16} className="transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-6 border-t p-3">{details}</CollapsibleContent>
      </Collapsible>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">{t("disputes:mediation.conversation")}</h3>
        <RefundRequestConversation request={request} />
      </section>

      {canRespond && request.can_send_message && <MessageComposer request={request} />}

      {canRespond && (
        <div className="sticky bottom-0 -mx-6 -mb-6 flex items-center justify-between gap-3 border-t bg-background p-4">
          <span className="text-sm text-muted-foreground">{t("disputes:mediation.approve_anytime")}</span>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={onApprove}>
            {t("disputes:detail.approve")}
          </Button>
        </div>
      )}
    </>
  );
}
